import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiService } from "@/api/api";
import Navbar from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import {
  Zap,
  BookOpen,
  Building2,
  Check,
  ArrowLeft,
  Loader2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

type DocumentationStyle = 'minimalist' | 'narrative' | 'clinical';

interface StyleOption {
  id: DocumentationStyle;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  sections: {
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
  };
}

const STYLE_OPTIONS: StyleOption[] = [
  {
    id: 'minimalist',
    title: 'Minimalist',
    subtitle: 'Telegraphic shorthand for high-volume clinics.',
    icon: <Zap className="h-5 w-5" />,
    sections: {
      subjective: `Pt reports "doing okay." Mood stable. Sleep 5hrs (broken). Denies SI/HI. Work stress ongoing. Appetite fair. Energy low. Compliance with meds - yes.`,
      objective: `Affect blunted. Speech normal rate/vol. Thought process linear. No acute distress. Grooming appropriate. Eye contact fair. Psychomotor normal.`,
      assessment: `F33.1 MDD, moderate, recurrent. Partial response to current regimen. Sleep disturbance persists. No safety concerns.`,
      plan: `Continue Sertraline 100mg daily. Add sleep hygiene education. CBT homework reviewed. Follow up 4wks. PRN Hydroxyzine for anxiety.`
    }
  },
  {
    id: 'narrative',
    title: 'Narrative (Standard)',
    subtitle: 'Cohesive paragraphs focusing on the patient\'s story.',
    icon: <BookOpen className="h-5 w-5" />,
    sections: {
      subjective: `Patient describes feeling "a bit overwhelmed" this week due to ongoing work stress, though they note their overall mood has been generally stable compared to previous visits. They report sleeping approximately 6 hours per night but continue to experience frequent awakenings. Appetite has been fair with no significant changes in weight. The patient expressly denies any suicidal ideation, homicidal ideation, or self-harm urges. They report good compliance with their current medication regimen.`,
      objective: `The patient appeared well-groomed and was appropriately dressed for the weather. They maintained good eye contact throughout the session. Their affect was congruent with their reported mood, displaying appropriate emotional range. Speech was spontaneous, with regular rate and rhythm. Thought process remained goal-directed and logical throughout the session. No psychomotor agitation or retardation was observed.`,
      assessment: `Major Depressive Disorder, recurrent episode, moderate severity (F33.1). The patient shows continued improvement from baseline with some residual symptoms, particularly in the areas of sleep and energy. Current treatment approach appears to be effective, though optimization may be beneficial.`,
      plan: `Continue current medication regimen with Sertraline 100mg daily. Discussed sleep hygiene strategies and provided educational materials. Encouraged continued engagement with cognitive behavioral therapy techniques practiced in previous sessions. Patient agrees to maintain mood journal. Follow-up appointment scheduled in 4 weeks to assess progress.`
    }
  },
  {
    id: 'clinical',
    title: 'Clinical Format',
    subtitle: 'Formal terminology and structured MSE for rigor.',
    icon: <Building2 className="h-5 w-5" />,
    sections: {
      subjective: `CHIEF COMPLAINT: "Feeling overwhelmed with work"
HISTORY OF PRESENT ILLNESS: Patient reports euthymic mood baseline with transient situational stressors related to occupational demands. Endorses mid-terminal insomnia with approximately 5-6 hours of fragmented sleep. Denies SI/HI/AV. Reports medication compliance. No recent changes in appetite or weight. Energy level subjectively decreased.`,
      objective: `MENTAL STATUS EXAMINATION:
Appearance: Appropriate grooming and attire, age-appropriate
Behavior: Cooperative, engaged, psychomotor activity within normal limits
Speech: Regular rate, rhythm, and volume; spontaneous
Mood: "Okay" (patient's words)
Affect: Full range, mood-congruent, appropriate reactivity
Thought Process: Linear, logical, goal-directed
Thought Content: Denies SI/HI/AV, no delusions elicited
Perception: Denies AVH
Cognition: Alert and oriented x4, attention intact
Insight/Judgment: Fair/Fair`,
      assessment: `DIAGNOSES:
1. Major Depressive Disorder, Recurrent, Moderate (F33.1) - Partial remission on current regimen
2. Insomnia Disorder, comorbid (F51.01)

RISK ASSESSMENT: Low acute risk. Protective factors include treatment engagement, social support, future orientation.`,
      plan: `PHARMACOTHERAPY: Continue Sertraline 100mg PO daily
PSYCHOTHERAPY: Reinforce CBT techniques; sleep restriction therapy discussed
PATIENT EDUCATION: Sleep hygiene handout provided
SAFETY PLAN: Reviewed; patient verbalizes understanding
FOLLOW-UP: 4 weeks; sooner PRN for worsening symptoms
LABS: None indicated at this time`
    }
  }
];

const SOAPFormatSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [selectedStyle, setSelectedStyle] = useState<DocumentationStyle>('narrative');
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadPreferences = async () => {
      setIsLoading(true);
      try {
        const savedStyle = localStorage.getItem('soap_documentation_style');
        if (savedStyle) {
          setSelectedStyle(savedStyle as DocumentationStyle);
          const index = STYLE_OPTIONS.findIndex(s => s.id === savedStyle);
          if (index !== -1) setCurrentIndex(index);
        }
      } catch (error) {
        console.error("Error loading preferences:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadPreferences();
  }, []);

  const handlePrev = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
    setCurrentIndex(newIndex);
    setSelectedStyle(STYLE_OPTIONS[newIndex].id);
  };

  const handleNext = () => {
    const newIndex = currentIndex < STYLE_OPTIONS.length - 1 ? currentIndex + 1 : currentIndex;
    setCurrentIndex(newIndex);
    setSelectedStyle(STYLE_OPTIONS[newIndex].id);
  };

  const handleCardClick = (index: number) => {
    if (index !== currentIndex) {
      setCurrentIndex(index);
      setSelectedStyle(STYLE_OPTIONS[index].id);
    }
  };

  const handleSaveConfiguration = async () => {
    setIsSaving(true);
    try {
      // Update caregiver profile via API
      await apiService.updateCaregiverProfile({
        soap_documentation_style: selectedStyle
      });

      // Also save to localStorage for quick access
      localStorage.setItem('soap_documentation_style', selectedStyle);

      toast({
        title: "Settings Saved",
        description: "Your documentation style has been updated.",
        variant: "default",
      });

      // Navigate to home page after saving
      setTimeout(() => {
        navigate('/');
      }, 500);
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="h-8 w-8 animate-spin text-[#005EB8]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="mobile-container w-full py-4 sm:py-6">
        <Navbar />

        <main className="flex-1 mt-4 sm:mt-6">
          <div className="max-w-6xl mx-auto">
            {/* Header Card */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </button>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>{currentIndex + 1} / {STYLE_OPTIONS.length}</span>
                </div>
              </div>

              <h2 className="text-2xl font-bold mb-1">Documentation Styles</h2>
              <p className="text-gray-500 text-sm">
                Choose the voice and structure of your AI-generated notes.
              </p>
            </div>

            {/* Carousel Container */}
            <div className="relative py-4" style={{ perspective: '1000px' }}>
              {/* Cards Container */}
              <div className="relative h-[580px] overflow-visible">
                {STYLE_OPTIONS.map((option, index) => {
                  const offset = index - currentIndex;
                  const isActive = index === currentIndex;
                  const isVisible = Math.abs(offset) <= 1;

                  if (!isVisible) return null;

                  return (
                    <div
                      key={option.id}
                      onClick={() => handleCardClick(index)}
                      className={`absolute inset-x-12 top-0 bottom-0 transition-all duration-400 ease-out ${!isActive ? 'cursor-pointer' : ''}`}
                      style={{
                        transform: `translateX(${offset * 85}%) scale(${isActive ? 1 : 0.9})`,
                        opacity: isActive ? 1 : 0.4,
                        zIndex: isActive ? 10 : 5,
                      }}
                    >
                      <div
                        className={`bg-white rounded-xl p-6 h-full overflow-y-auto transition-shadow duration-300 border ${
                          isActive
                            ? 'shadow-xl border-gray-200'
                            : 'shadow-md border-gray-100'
                        }`}
                      >
                        {/* Style Header */}
                        <div className="flex items-center gap-3 mb-6">
                          <div className="p-3 rounded-xl bg-gray-100 text-gray-600">
                            {option.icon}
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {option.title}
                            </h3>
                            <p className="text-sm text-gray-500">{option.subtitle}</p>
                          </div>
                        </div>

                        {/* SOAP Sections */}
                        <div className="space-y-4">
                          <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-gray-50 to-white">
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <span className="w-6 h-6 rounded bg-blue-100 text-blue-600 text-xs flex items-center justify-center font-bold">S</span>
                              Subjective
                            </h4>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {option.sections.subjective}
                            </p>
                          </div>

                          <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-gray-50 to-white">
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <span className="w-6 h-6 rounded bg-green-100 text-green-600 text-xs flex items-center justify-center font-bold">O</span>
                              Objective
                            </h4>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {option.sections.objective}
                            </p>
                          </div>

                          <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-gray-50 to-white">
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <span className="w-6 h-6 rounded bg-purple-100 text-purple-600 text-xs flex items-center justify-center font-bold">A</span>
                              Assessment
                            </h4>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {option.sections.assessment}
                            </p>
                          </div>

                          <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-gray-50 to-white">
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                              <span className="w-6 h-6 rounded bg-orange-100 text-orange-600 text-xs flex items-center justify-center font-bold">P</span>
                              Plan
                            </h4>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {option.sections.plan}
                            </p>
                          </div>

                          {/* Save Button inside card */}
                          {isActive && (
                            <div className="flex justify-center mt-6">
                              <Button
                                onClick={handleSaveConfiguration}
                                disabled={isSaving}
                                className="bg-[#005EB8] hover:bg-[#004a93] text-white px-8"
                              >
                                {isSaving ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving...
                                  </>
                                ) : (
                                  <>
                                    <Check className="h-4 w-4 mr-2" />
                                    Save Settings
                                  </>
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Navigation Controls */}
              <div className="flex items-center justify-center gap-6 mt-8">
                <button
                  onClick={handlePrev}
                  disabled={currentIndex === 0}
                  className={`p-2 rounded-full transition-all ${
                    currentIndex === 0
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>

                {/* Dot Indicators */}
                <div className="flex items-center gap-2">
                  {STYLE_OPTIONS.map((option, index) => (
                    <button
                      key={option.id}
                      onClick={() => handleCardClick(index)}
                      className={`rounded-full transition-all duration-300 ${
                        index === currentIndex
                          ? 'bg-[#005EB8] w-8 h-2.5'
                          : 'bg-gray-300 hover:bg-gray-400 w-2.5 h-2.5'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={handleNext}
                  disabled={currentIndex === STYLE_OPTIONS.length - 1}
                  className={`p-2 rounded-full transition-all ${
                    currentIndex === STYLE_OPTIONS.length - 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SOAPFormatSettings;
